/**
 * Mock Database Service
 * Gerencia o estado global da aplicação usando localStorage
 * Simula um banco de dados compartilhado entre ClientDashboard e AdminClientView
 */

export interface ClienteData {
  id: string;
  saldoLivre: number;
  saldoVinculado: number;
  totalInvestidoMudas: number;
  totalInvestidoSocial: number;
  destinacaoConfirmada: boolean;
  transacoes: Array<{
    id: string;
    dataHora: string;
    origem: string;
    tipo: string;
    destino: string;
    valor: number;
    tipoTransacao: "entrada" | "saida";
  }>;
  resumoDestinacoes: Array<{
    id: string;
    tipo: 'mudas' | 'social';
    valor: number;
    regiao: string;
    projeto: string;
  }>;
  viagens: Array<{
    id: string;
    data: string;
    rota: string;
    cashbackTotal: number;
    fundoMudas: number;
    projetosSociais: number;
    saldoCarteira: number;
    statusRepasse: 'pendente' | 'parcial' | 'concluido';
  }>;
}

const STORAGE_KEY = 'banclima_mock_db';
const CLIENT_ID = '1'; // ID do cliente João Silva

/**
 * Estado inicial padrão
 */
const getInitialState = (): Record<string, ClienteData> => {
  return {
    [CLIENT_ID]: {
      id: CLIENT_ID,
      saldoLivre: 0.00,
      saldoVinculado: 20.00, // Cashback pendente
      totalInvestidoMudas: 0.00,
      totalInvestidoSocial: 0.00,
      destinacaoConfirmada: false,
      transacoes: [
        {
          id: 'trans-1',
          dataHora: "20/12/2025 16:15",
          origem: "Voo SDU-GRU",
          tipo: "Entrada",
          destino: "Cashback de Viagem",
          valor: 20.00,
          tipoTransacao: "entrada"
        }
      ],
      resumoDestinacoes: [],
      viagens: [
        {
          id: 'viagem-1',
          data: "20/12/2025",
          rota: "SDU - GRU",
          cashbackTotal: 20.00,
          fundoMudas: 0.00,
          projetosSociais: 0.00,
          saldoCarteira: 20.00,
          statusRepasse: 'pendente'
        }
      ]
    }
  };
};

/**
 * Carrega dados do localStorage ou retorna estado inicial
 */
export const loadData = (): Record<string, ClienteData> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Erro ao carregar dados do localStorage:', error);
  }
  return getInitialState();
};

/**
 * Salva dados no localStorage
 */
const saveData = (data: Record<string, ClienteData>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    // Dispara evento customizado para notificar outras abas/componentes
    window.dispatchEvent(new Event('mockDB:updated'));
  } catch (error) {
    console.error('Erro ao salvar dados no localStorage:', error);
  }
};

/**
 * Obtém dados de um cliente específico
 */
export const getDadosCliente = (clientId: string = CLIENT_ID): ClienteData | null => {
  const data = loadData();
  return data[clientId] || null;
};

/**
 * Atualiza dados de um cliente
 */
const updateClienteData = (clientId: string, updates: Partial<ClienteData>): void => {
  const data = loadData();
  if (!data[clientId]) {
    data[clientId] = getInitialState()[CLIENT_ID];
  }
  data[clientId] = { ...data[clientId], ...updates };
  saveData(data);
};

/**
 * Registra uma compensação (confirmação de destinação inicial)
 */
export const registrarCompensacao = (
  clientId: string,
  mudasValue: number,
  socialValue: number,
  mudasRegion: string,
  mudasProject: string,
  socialRegion: string,
  socialProject: string
): void => {
  const cliente = getDadosCliente(clientId);
  if (!cliente) return;

  const agora = new Date();
  const dataHoraFormatada = agora.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Adiciona transações
  const novasTransacoes = [
    {
      id: `trans-${Date.now()}-1`,
      dataHora: dataHoraFormatada,
      origem: "Voo SDU-GRU",
      tipo: "Apoio a Mudas",
      destino: `${mudasRegion} - ${mudasProject}`,
      valor: mudasValue,
      tipoTransacao: "saida" as const
    },
    {
      id: `trans-${Date.now()}-2`,
      dataHora: dataHoraFormatada,
      origem: "Voo SDU-GRU",
      tipo: "Projetos Sociais",
      destino: `${socialRegion} - ${socialProject}`,
      valor: socialValue,
      tipoTransacao: "saida" as const
    }
  ];

  // Adiciona ao resumo de destinações
  const novoResumo = [
    {
      id: `mudas-${Date.now()}`,
      tipo: 'mudas' as const,
      valor: mudasValue,
      regiao: mudasRegion,
      projeto: mudasProject
    },
    {
      id: `social-${Date.now()}`,
      tipo: 'social' as const,
      valor: socialValue,
      regiao: socialRegion,
      projeto: socialProject
    }
  ];

  // Calcula novo saldo
  const totalDestinado = mudasValue + socialValue;
  const novoSaldoLivre = cliente.saldoVinculado - totalDestinado; // O que sobra vai para saldo livre
  const novoSaldoVinculado = 0; // Todo o saldo vinculado foi destinado

  // Atualiza viagem
  const viagemAtualizada = {
    ...cliente.viagens[0],
    fundoMudas: mudasValue,
    projetosSociais: socialValue,
    saldoCarteira: novoSaldoLivre, // Saldo livre disponível
    statusRepasse: 'concluido' as const
  };

  updateClienteData(clientId, {
    saldoLivre: novoSaldoLivre,
    saldoVinculado: novoSaldoVinculado,
    totalInvestidoMudas: mudasValue,
    totalInvestidoSocial: socialValue,
    destinacaoConfirmada: true,
    transacoes: [...novasTransacoes, ...cliente.transacoes],
    resumoDestinacoes: novoResumo,
    viagens: [viagemAtualizada, ...cliente.viagens.slice(1)]
  });
};

/**
 * Adiciona uma doação livre
 */
export const adicionarDoacao = (
  clientId: string,
  valor: number,
  tipo: string,
  destino: string
): void => {
  const cliente = getDadosCliente(clientId);
  if (!cliente || valor > cliente.saldoLivre) return;

  const agora = new Date();
  const dataHoraFormatada = agora.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Nova transação
  const novaTransacao = {
    id: `trans-${Date.now()}`,
    dataHora: dataHoraFormatada,
    origem: "Saldo Livre",
    tipo: tipo,
    destino: destino,
    valor: valor,
    tipoTransacao: "saida" as const
  };

  // Extrai região e projeto do destino
  const partes = destino.split(' - ');
  let regiao = '';
  let projeto = '';
  
  if (partes.length >= 3) {
    regiao = partes[1];
    projeto = partes[2];
  } else if (partes.length === 2) {
    regiao = partes[0];
    projeto = partes[1];
  } else {
    projeto = destino;
  }

  const tipoResumo: 'mudas' | 'social' = tipo === 'Apoio a Mudas' ? 'mudas' : 'social';

  // Nova destinação no resumo
  const novaDestinacao = {
    id: `${tipoResumo}-${Date.now()}`,
    tipo: tipoResumo,
    valor: valor,
    regiao: regiao,
    projeto: projeto
  };

  // Atualiza métricas
  const novoTotalMudas = tipo === 'Apoio a Mudas' 
    ? cliente.totalInvestidoMudas + valor 
    : cliente.totalInvestidoMudas;
  const novoTotalSocial = tipo === 'Projetos Sociais'
    ? cliente.totalInvestidoSocial + valor
    : cliente.totalInvestidoSocial;

  // Atualiza viagem
  const viagemAtualizada = {
    ...cliente.viagens[0],
    fundoMudas: novoTotalMudas,
    projetosSociais: novoTotalSocial,
    saldoCarteira: cliente.saldoLivre - valor
  };

  updateClienteData(clientId, {
    saldoLivre: cliente.saldoLivre - valor,
    totalInvestidoMudas: novoTotalMudas,
    totalInvestidoSocial: novoTotalSocial,
    transacoes: [novaTransacao, ...cliente.transacoes],
    resumoDestinacoes: [...cliente.resumoDestinacoes, novaDestinacao],
    viagens: [viagemAtualizada, ...cliente.viagens.slice(1)]
  });
};

/**
 * Atualiza métricas específicas
 */
export const atualizarMetricas = (
  clientId: string,
  tipo: 'mudas' | 'social',
  valor: number
): void => {
  const cliente = getDadosCliente(clientId);
  if (!cliente) return;

  const updates: Partial<ClienteData> = {};
  if (tipo === 'mudas') {
    updates.totalInvestidoMudas = cliente.totalInvestidoMudas + valor;
  } else {
    updates.totalInvestidoSocial = cliente.totalInvestidoSocial + valor;
  }

  updateClienteData(clientId, updates);
};

/**
 * Reseta dados do cliente (útil para testes)
 */
export const resetClienteData = (clientId: string = CLIENT_ID): void => {
  const data = loadData();
  data[clientId] = getInitialState()[CLIENT_ID];
  saveData(data);
};
